/**
 * The decisions the log viewers make, separated from the JSX that renders them.
 *
 * Deliberately dependency-free (no React, no query client, no design system) so
 * each rule can be exercised directly. These are the choices that change what an
 * operator sees or is allowed to see, and every one of them fails silently when
 * wrong: a scope that a toolbar can widen still renders a plausible page of
 * somebody else's rows, and an unpriced call rendered as $0.00 still adds up to
 * a total that looks measured.
 */

import type {
  ActivityQueryParams,
  ActivityScope,
  AiLogQueryParams,
  AiLogScope,
} from '@command-center/logging-sdk';
import { applyActivityScope, applyAiLogScope } from '@command-center/logging-sdk';
import type { FilterField } from './LogFilterBar';

/**
 * Merge toolbar values with the portal's enforced scope.
 *
 * Scope goes last, through the same `applyActivityScope` the Worker uses, so
 * the browser cannot even ask for rows outside it. This is convenience rather
 * than the boundary — the Worker applies the rule again to whatever arrives —
 * but it keeps the URL, the query key, and the request in agreement.
 */
export function composeActivityParams(
  values: Record<string, string | undefined>,
  scope: ActivityScope | undefined,
  limit: number,
): ActivityQueryParams {
  return { ...applyActivityScope(values as ActivityQueryParams, scope), limit };
}

export function composeAiLogParams(
  values: Record<string, string | undefined>,
  scope: AiLogScope | undefined,
): AiLogQueryParams {
  return applyAiLogScope(values as AiLogQueryParams, scope);
}

/**
 * Which filter controls to render.
 *
 * A dimension the portal has pinned is not a filter: the toolbar cannot widen
 * it, so the control could only ever narrow to nothing or do nothing. Dropping
 * it is more honest than rendering a select that silently has no effect.
 */
export function visibleFilters(
  all: FilterField[],
  hidden: string[],
  scope: object | undefined,
): FilterField[] {
  const pinned = new Set(Object.keys(scope ?? {}).filter((key) => {
    const value = (scope as Record<string, unknown>)[key];
    return value !== undefined && value !== null && value !== '';
  }));
  return all.filter((field) => !hidden.includes(field.key) && !pinned.has(field.key));
}

/**
 * Build the Organization / App selectors for the toolbar.
 *
 * A portal with a directory gets readable names; one whose directory failed to
 * load gets a free-text id field, so a lookup outage degrades filtering rather
 * than removing it; a portal that passes no directory at all gets neither,
 * because it has nothing to choose between.
 */
export function directoryField(
  key: string,
  label: string,
  options: Array<{ value: string; label: string }> | undefined,
): FilterField | null {
  if (!options) return null;
  return options.length
    ? { key, label, options }
    : { key, label, placeholder: `${label} id` };
}

export interface CostDisplay {
  known: boolean;
  label: string;
}

/**
 * How a call's cost reads.
 *
 * `cost_micro_usd` null and `cost_cents` zero-or-null means the call was never
 * priced, which is NOT a call that cost nothing. It has to say so: a spend
 * total built from rows silently read as zero is a lower bound wearing the
 * costume of a measurement.
 */
export function describeCost(
  row: { cost_micro_usd?: number | null; cost_cents?: number | null },
  format: (microUsd: number) => string,
  resolve: (row: { cost_micro_usd?: number | null; cost_cents?: number | null }) => number,
): CostDisplay {
  const known = row.cost_micro_usd != null || (row.cost_cents ?? 0) > 0;
  return known
    ? { known: true, label: format(resolve(row)) }
    : { known: false, label: 'cost unknown' };
}

export type LogViewState = 'error' | 'loading' | 'empty' | 'ready';

/**
 * Which of the four states a list is in.
 *
 * They are genuinely four, not two: "we could not ask" (error), "we have not
 * asked yet" (loading) and "we asked and there is nothing" (empty) mean
 * different things to an operator, and collapsing any pair of them into one
 * blank panel is how an outage reads as a quiet week. `degraded` is separate
 * again and rides alongside whichever of these applies — the store answered,
 * but told us not to trust the answer.
 */
export function logViewState(query: {
  isError: boolean;
  isLoading: boolean;
  itemCount: number;
}): LogViewState {
  if (query.isError) return 'error';
  if (query.isLoading) return 'loading';
  return query.itemCount === 0 ? 'empty' : 'ready';
}
